'use client';
import React, { useCallback } from 'react';

import Layout from '../lib/components/Layout';
import ColumnsDefinition from './ColumnsDef';
import { injectQuotes, sortFDAData } from '../lib/utils';

import { ICalendarProps, IDateRange } from '../lib/interfaces';
import styled from '@benzinga/themetron';
import { Checkbox } from '@benzinga/core-ui';
import { commaSeparatedSymbols } from '@benzinga/utils';
import { useQueryParams } from '@benzinga/filter-ui';
import { GridApi } from 'ag-grid-community';
import { FDA, FdaCalendarParams, ISetParams } from '@benzinga/calendar-manager';
import { SessionContext } from '@benzinga/session-context';
import { fetchFDA, FDACalendarTabKeys, FDACalendarTabKeysType } from './Services';
import { CALENDAR_FETCH_PAGE_SIZE, useCalendarPagedFetch } from '../lib/useCalendarPagedFetch';
import { DateTime } from 'luxon';

export const fetchFDAData = fetchFDA;
export const FDAColumnsDef = ColumnsDefinition;

const getTabDataByTabIndex = (index: number, dataTabs: [FDA[], FDA[], FDA[], FDA[]]) => {
  return dataTabs[index];
};

export const FDACalendar: React.FC<ICalendarProps> = ({
  calendar,
  calendarData,
  calendarType,
  hasStockTableStyling,
  height,
  hiddenColumns,
  initialData,
  initialDateRange,
  initialTab,
  initialTicker,
  ...props
}) => {
  const session = React.useContext(SessionContext);
  const [queryParams, updateQueryParams] = useQueryParams();
  const activeTabKeyRef = React.useRef<FDACalendarTabKeysType>(FDACalendarTabKeys[0]);
  const [gridApi, setGridApi] = React.useState<GridApi | null>(null);
  const [activeTabIndex, setActiveTabIndex] = React.useState(getTabIndexByKey(initialTab) || 0);
  const [binaryEventsOnly, setBinaryEventsOnly] = React.useState(queryParams?.binaryOnly ?? false);
  const [historicalData, setHistoricalData] = React.useState<FDA[]>(() => {
    const sortedData = sortFDAData(initialData, binaryEventsOnly);
    return sortedData.historical || [];
  });
  // const [todayData, setTodayData] = React.useState<any>([]);
  const [exactTargetDateData, setExactTargetDateData] = React.useState<FDA[]>(() => {
    const sortedData = sortFDAData(initialData, binaryEventsOnly);
    return sortedData.exact || [];
  });
  const [estimatedDatesData, setEstimatedDatesData] = React.useState<FDA[]>(() => {
    const sortedData = sortFDAData(initialData, binaryEventsOnly);
    return sortedData.estimated || [];
  });

  const [pdufaDateData, setPdufaDateData] = React.useState<FDA[]>(() => {
    const sortedData = sortFDAData(initialData, binaryEventsOnly);
    return sortedData.pdufa || [];
  });

  const dataTabs: [FDA[], FDA[], FDA[], FDA[]] = React.useMemo(
    () => [historicalData, exactTargetDateData, estimatedDatesData, pdufaDateData],
    [historicalData, exactTargetDateData, estimatedDatesData, pdufaDateData],
  );
  const [rowData, setRowData] = React.useState(() => {
    const tabData = getTabDataByTabIndex(activeTabIndex, dataTabs);
    return tabData || [];
  });
  const [currentFilteredRowsCount, setCurrentFilteredRowsCount] = React.useState(null);

  const now = DateTime.local();
  const dateRange: IDateRange = {
    date_from: initialDateRange?.date_from ?? now.startOf('month').toISODate(),
    date_to: initialDateRange?.date_to ?? now.endOf('month').toISODate(),
  };

  const [filters, setFilters] = React.useState<FdaCalendarParams>({
    dateFrom: dateRange.date_from,
    dateTo: dateRange.date_to,
    securities: commaSeparatedSymbols(queryParams?.securities ?? queryParams?.symbol ?? initialTicker),
  });

  React.useEffect(() => {
    if (calendarData?.serverSide) {
      setTableData(getTabDataByTabIndex(activeTabIndex, dataTabs));
    }
  }, [initialData, calendarData, activeTabIndex, dataTabs]);

  const loadQuotesForData = useCallback(
    async (data: FDA[]) => {
      const dataTickers: string[] = [];

      if (Array.isArray(data)) {
        data.forEach(item => {
          if (Array.isArray(item.companies) && item.companies.length) {
            const ticker = item.companies[0].securities?.[0]?.symbol;

            if (ticker) {
              item.ticker = ticker;
              dataTickers.push(ticker);
            }
          }
        });
      }

      return injectQuotes(data, dataTickers, session.getSession());
    },
    [session],
  );

  const setFDAData = React.useCallback(
    async (items: FDA[]) => {
      if (!items || items.length === 0) {
        setHistoricalData([]);
        setExactTargetDateData([]);
        setEstimatedDatesData([]);
        setPdufaDateData([]);
        return;
      }

      const fdaData = sortFDAData(items, binaryEventsOnly);

      setHistoricalData(fdaData.historical);
      setExactTargetDateData(fdaData.exact);
      setEstimatedDatesData(fdaData.estimated);
      setPdufaDateData(fdaData.pdufa);

      fdaData.exact = (await loadQuotesForData(fdaData.exact)) as FDA[];

      setExactTargetDateData(fdaData.exact);
    },
    [binaryEventsOnly, loadQuotesForData],
  );

  const activeTabKey = FDACalendarTabKeys[activeTabIndex];
  activeTabKeyRef.current = activeTabKey;

  const {
    loading: isLoading,
    onFilterChanged: onPagedFilterChanged,
    onViewportChanged,
    tableData: pagedRawData,
  } = useCalendarPagedFetch<FDA, FdaCalendarParams>({
    fetchPage: (params, page) =>
      fetchFDAData(
        session.getSession(),
        { ...params, page, pageSize: CALENDAR_FETCH_PAGE_SIZE },
        activeTabKeyRef.current,
      ),
    initialData,
    initialParams: filters,
  });

  React.useEffect(() => {
    void setFDAData(pagedRawData);
  }, [pagedRawData, setFDAData]);

  const fetchData = (filterParams: FdaCalendarParams, tabKey?: FDACalendarTabKeysType) => {
    if (tabKey) {
      activeTabKeyRef.current = tabKey;
    }
    onPagedFilterChanged(filterParams);
  };

  // React.useEffect(() => {
  //   if (tableReady) {
  //     fetchData(filters, activeTabKey);
  //   }
  // }, [activeTabKey, binaryEventsOnly, fetchData, filters, tableReady]);

  React.useEffect(() => {
    setTableData(getTabDataByTabIndex(activeTabIndex, dataTabs));
  }, [activeTabIndex, dataTabs]);

  React.useEffect(() => {
    if (gridApi) {
      const toggleCatalystColumn = (index: number) => {
        switch (index) {
          case 0:
            gridApi.setColumnsVisible(['historical_date'], true);
            gridApi.setColumnsVisible(['estimated_date', 'exact_date', 'quote.last', 'quote.changePercent'], false);
            gridApi.applyColumnState({
              defaultState: { sort: null },
              state: [{ colId: 'historical_date', sort: 'desc' }],
            });
            break;
          case 1:
          case 3:
            gridApi.setColumnsVisible(['historical_date', 'estimated_date'], false);
            gridApi.setColumnsVisible(['exact_date', 'quote.last', 'quote.changePercent'], true);
            gridApi.applyColumnState({
              defaultState: { sort: null },
              state: [{ colId: 'exact_date', sort: 'asc' }],
            });
            break;
          case 2:
            gridApi.setColumnsVisible(
              ['historical_date', 'estimated_date', 'quote.last', 'quote.changePercent'],
              false,
            );
            gridApi.setColumnsVisible(['exact_date'], true);
            gridApi.applyColumnState({
              defaultState: { sort: null },
              state: [{ colId: 'exact_date', sort: 'asc' }],
            });
            break;
          default:
            break;
        }
        gridApi.sizeColumnsToFit();
      };

      toggleCatalystColumn(activeTabIndex);
    }
  }, [activeTabIndex, gridApi]);

  const onTableReady = (params: { api: GridApi }) => {
    if (initialData) {
      setTableData(getTabDataByTabIndex(activeTabIndex, dataTabs));
    } else {
      const fetchParams: FdaCalendarParams = {
        ...(filters.dateFrom && { dateFrom: filters.dateFrom }),
        ...(filters.dateTo && { dateTo: filters.dateTo }),
        ...(filters.securities && { securities: filters.securities }),
      };
      fetchData(fetchParams, activeTabKey);
    }
    setGridApi(params.api);
  };

  const getTabDataCount = useCallback(
    (index: number) => {
      if (isLoading) return '-';

      const tabData = getTabDataByTabIndex(index, dataTabs);

      if (index === activeTabIndex) {
        if (currentFilteredRowsCount) {
          return currentFilteredRowsCount;
        }
      }

      return tabData?.length;
    },
    [activeTabIndex, currentFilteredRowsCount, dataTabs, isLoading],
  );

  function tabs(stat = true): any {
    const isHistoricalTab = activeTabIndex === 0;
    return [
      {
        key: FDACalendarTabKeys[0],
        title: isHistoricalTab && stat ? `Historical (${getTabDataCount(0)})` : `Historical`,
        tooltip: `Tab shows events that have happened in the past or which have no catalyst date`,
      },
      // {
      //   key: 'today',
      //   title: stat ? `Today (${getTabDataCount(1)})` : `Today`,
      //   tooltip: `Tab shows events on today`,
      // },
      {
        key: FDACalendarTabKeys[1],
        title: stat && !isHistoricalTab ? `Exact Dates (${getTabDataCount(1)})` : `Exact Dates`,
        tooltip: `Tab shows events with exact dates in the future`,
      },
      {
        key: FDACalendarTabKeys[2],
        title: stat && !isHistoricalTab ? `Estimated Dates (${getTabDataCount(2)})` : `Estimated Dates`,
        tooltip: `Tab shows future events expressed as a timeframe rather than an exact date`,
      },
      {
        key: FDACalendarTabKeys[3],
        title: stat && !isHistoricalTab ? `PDUFA (${getTabDataCount(3)})` : `PDUFA`,
        tooltip: ``,
      },
    ];
  }

  async function onTabChange(index: number) {
    if (activeTabIndex === index) return;

    const newTabKey = FDACalendarTabKeys[index];

    setActiveTabIndex(index);

    const fetchParams: FdaCalendarParams = {
      ...(filters.dateFrom && { dateFrom: filters.dateFrom }),
      ...(filters.dateTo && { dateTo: filters.dateTo }),
      ...(filters.securities && { securities: filters.securities }),
    };

    if (activeTabKey === 'historical') {
      await fetchData(fetchParams, newTabKey);
    } else if (newTabKey === 'historical') {
      await fetchData(fetchParams, newTabKey);
    }

    const data = getTabDataByTabIndex(index, dataTabs);
    if (data && data.length) {
      setTableData(data);
    }

    if (props.onTabChange) {
      const tabKey = tabs()?.[index]?.key;

      if (tabKey) {
        props.onTabChange(tabKey);
      }
    }

    if (!gridApi?.isAnyFilterPresent()) {
      setCurrentFilteredRowsCount(null);
    } else {
      updateFilteredRowsCount(gridApi);
    }
  }

  function setTableData(data: FDA[]) {
    setRowData(data);
  }

  const onFilterChanged = (params: ISetParams) => {
    const fdaParams = params as FdaCalendarParams;
    setFilters(fdaParams);
    activeTabKeyRef.current = activeTabKey;
    onPagedFilterChanged(fdaParams);
  };

  function getTabIndexByKey(key?: string | number): number {
    if (key) {
      return FDACalendarTabKeys.findIndex(tabKey => {
        return tabKey === key;
      });
    } else {
      return 0;
    }
  }

  const handleBinaryOnly = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event?.target?.checked;

    if (binaryEventsOnly !== value) {
      setBinaryEventsOnly(value);
      updateQueryParams({ binaryOnly: value }, 'replace');
    }
  };

  const BinaryEventsButton = () => {
    return (
      <BinaryEventsButtonWrapper>
        <Checkbox
          checked={binaryEventsOnly}
          id="binary-events-only"
          label="Binary Events Only"
          onChange={event => handleBinaryOnly(event)}
        />
      </BinaryEventsButtonWrapper>
    );
  };

  const updateFilteredRowsCount = (api: GridApi | null) => {
    if (!api) return;

    const counts = api?.isAnyFilterPresent() ? api.getDisplayedRowCount() : null;

    setCurrentFilteredRowsCount(counts as any);
  };

  const onGridFilterChanged = ({ api }: { api: GridApi }) => {
    updateFilteredRowsCount(api);
  };

  const onRowDataChanged = ({ api }: { api: GridApi }) => {
    updateFilteredRowsCount(api);
  };

  return (
    <Container>
      <Layout
        calendar={calendar}
        calendarData={calendarData}
        calendarType={calendarType}
        columnDefs={ColumnsDefinition}
        disableSettingGridApi={true}
        enableDatePicker={true}
        enableSearch
        filterBarChildren={<BinaryEventsButton />}
        hasStockTableStyling={hasStockTableStyling}
        height={height}
        hiddenColumns={hiddenColumns}
        initialDateRange={dateRange}
        initialTabIndex={activeTabIndex}
        onFilterChanged={onFilterChanged}
        onGridFilterChanged={onGridFilterChanged}
        onRowDataChanged={onRowDataChanged}
        onTabChange={onTabChange}
        onTableReady={onTableReady}
        rowHeight={80}
        searchKey="securities"
        tableData={rowData}
        tableLoading={isLoading}
        tabs={tabs()}
        theme="modern"
        ticker={queryParams?.symbol ?? initialTicker}
        viewportChange={onViewportChanged}
      />
    </Container>
  );
};

const Container = styled.div`
  .table-cell-outcome {
    white-space: normal;
  }

  .table-cell-get_alert {
    width: 70px;
  }
`;

const BinaryEventsButtonWrapper = styled('div')`
  margin-left: 30px;

  .checkbox-container {
    label {
      font-weight: 600;
      font-size: 14px;
      line-height: 20px;
      color: #283d59;
    }
  }

  .core-checkbox,
  input[type='checkbox'] {
    width: 18px;
    height: 18px;
    border: 2px solid #3f83f8;
    border-radius: 4px;
    accent-color: #3f83f8;
    cursor: pointer;
  }

  input[type='checkbox']:checked {
    background-color: #3f83f8;
  }

  @media screen and (max-width: 1000px) {
    margin-bottom: 10px;
    margin-left: 0;
    margin-top: 0;
  }
`;
